import {
  Button,
  Flex,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
  CTooltip,
} from "@coreui/react";
import {
  Alarm,
  AssignmentTurnedIn,
  DoDisturbOn,
  EventAvailable,
  Info,
} from "@mui/icons-material";

import {
  serviceOrdersByBranchID,
  serviceOrdersByCompanyID,
  listServiceOrders,
  serviceOrderToolsByServiceOrderId,
  sparesByServiceOrderID,
  serviceOrderMaintenanceRoutinesByServiceOrderId,
  serviceOrderEquipmentsByServiceOrderId,
} from "graphql/queries";
import {
  deleteServiceOrderEquipment,
  deleteServiceOrderMaintenanceRoutine,
  deleteServiceOrderTool,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { ServiceOrderCreateForm, ServiceOrderUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  getBranchName,
  getCompanyName,
  getUserEmail,
} from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import ExecutedServiceOrderContainer from "./ExecutedServiceOrder/ExecutedServiceOrderContainer";
import SelectUser from "components/users/SelectUser";
import { serviceOrderStatus } from "config/constants";
import { globalStrings } from "config/translations/es";
import ServiceOrderResume from "./ServiceOrderResume";
import ServiceOrderPhotos from "./ServiceOrderPhotos";
import MultipleSelect from "components/utils/MultipleSelect";

const ServiceOrders = () => {
  const { me } = useContext(LayoutContext);
  const [
    canCreate,
    canUpdate,
    canDelete,
    canExecute,
    canExport,
    canCancel,
    canReadPhotos,
    isTechnician,
  ] = usePermissions([
    "service-order:create",
    "service-order:update",
    "service-order:delete",
    "service-order:execute",
    "service-order:export",
    "service-order:cancel",
    "service-order:read-photo",
    "technician:role",
  ]);
  const [loading, setLoading] = useState(false);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [serviceOrdersLoading, setServiceOrdersLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    serviceOrderId: null,
    serviceOrderCode: null,
  });
  const [modalServiceOrder, setModalServiceOrder] = useState({
    visible: false,
    serviceOrderId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
    status: [],
  });
  const columnsMapped = useMemo(
    () =>
      serviceOrders.map((item) => ({
        ...item,
        orders: (
          <Grid>
            <span>{item.title}</span>
            <span className="text-muted" style={{ fontSize: "14px" }}>
              {item.companyName || <Loader />}
            </span>
            <span className="text-muted" style={{ fontSize: "14px" }}>
              {item.branchName || <Loader />}
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.start && moment(item.start).format("YYYY-MM-DD HH:mm")}
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.end && moment(item.end).format("YYYY-MM-DD HH:mm")}
            </span>
          </Grid>
        ),
        title: (
          <Grid>
            <span>{item.title}</span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.start && moment(item.start).format("YYYY-MM-DD HH:mm")}
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.end && moment(item.end).format("YYYY-MM-DD HH:mm")}
            </span>
          </Grid>
        ),
        companyName: item.companyName || <Loader />,
        branchName: item.branchName || <Loader />,
        userExecutorName: item.hasOwnProperty("userExecutorName") ? (
          item.userExecutorName
        ) : (
          <Loader />
        ),
        status: (
          <CTooltip content={globalStrings.serviceOrderStatus[item.status]}>
            {item.status === serviceOrderStatus.scheduled ? (
              <EventAvailable color="primary" />
            ) : item.status === serviceOrderStatus.executed ? (
              <AssignmentTurnedIn color="primary" />
            ) : item.status === serviceOrderStatus.canceled ? (
              <DoDisturbOn color="primary" />
            ) : item.status === serviceOrderStatus.executing ? (
              <Alarm color="primary" />
            ) : (
              <Info color="primary" />
            )}
          </CTooltip>
        ),
        actions: (
          <>
            {item.status !== serviceOrderStatus.executed && canUpdate && (
              <ActionIcon
                name={strings.serviceOrders.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            <ActionIcon
              name={strings.serviceOrders.resume}
              icon="Article"
              color="primary"
              onClick={() => openModal("resume", item.id)}
            />
            {canReadPhotos && (
              <ActionIcon
                name={strings.serviceOrders.readPhotos}
                icon="PhotoLibrary"
                color="primary"
                onClick={() => openModal("read-photo", item.id)}
              />
            )}
            {item.status !== serviceOrderStatus.executed && canExecute && (
              <ActionIcon
                name={strings.serviceOrders.execute}
                icon="Checklist"
                color="primary"
                onClick={() => openModal("execute", item.id)}
              />
            )}
            {canExport && (
              <ActionIcon
                name={strings.serviceOrders.export}
                icon="FileDownload"
                color="primary"
                onClick={() => openModal("export", item.id)}
              />
            )}
            {item.status !== serviceOrderStatus.executed &&
              item.status !== serviceOrderStatus.executing &&
              item.status !== serviceOrderStatus.canceled &&
              canCancel && (
                <ActionIcon
                  name={strings.serviceOrders.cancel}
                  icon="DoDisturbOn"
                  color="primary"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      serviceOrderId: item.id,
                      serviceOrderCode: item.code,
                      type: "cancel",
                    });
                  }}
                />
              )}
            {canDelete && (
              <ActionIcon
                name={strings.serviceOrders.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    serviceOrderId: item.id,
                    serviceOrderCode: item.code,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceOrders]
  );

  const onChangeStatus = (value) => {
    setSearchBy((prev) => ({ ...prev, status: value }));
  };

  const onNext = () => {
    fetchServiceOrders(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchServiceOrders(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchServiceOrders(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalServiceOrder({ visible: true, action, serviceOrderId: id });
  const onSuccessServiceOrder = () => {
    setModalServiceOrder({ visible: false });
    fetchServiceOrders();
  };
  const onSelectUser = (user) => {
    fetchServiceOrders(undefined, undefined, undefined, user);
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  async function fetchBranchName(id) {
    try {
      const branchData = await API.graphql(
        graphqlOperation(getBranchName, {
          id,
        })
      );
      const branchItem = branchData.data.getBranch;
      return branchItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchBranchName", err);
      return "";
    }
  }

  async function fetchUserEmail(id) {
    try {
      const userData = await API.graphql(
        graphqlOperation(getUserEmail, {
          id,
        })
      );
      const userItem = userData.data.getUser;
      return userItem?.email ?? "";
    } catch (err) {
      console.log("Error fetchUserEmail", err);
      return "";
    }
  }

  const getNamesByServiceOrders = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);
      const branchName = await fetchBranchName(item.branchID);
      const userExecutorName = item.userExecutor
        ? await fetchUserEmail(item.userExecutor)
        : "";

      setServiceOrders((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
          branchName,
          userExecutorName,
        };

        return updatedList;
      });
    });
  };

  async function fetchServiceOrders(
    token,
    isNext = true,
    keywords,
    userExecutorParam
  ) {
    let userExecutor = userExecutorParam;
    let objOperation = {};
    let nameOperation = listServiceOrders;
    let nameOperationStr = "listServiceOrders";
    try {
      setServiceOrdersLoading(true);

      if (!!searchBy.branch.id) {
        objOperation = {
          branchID: searchBy.branch.id,
          sortDirection: "DESC",
        };
        nameOperation = serviceOrdersByBranchID;
        nameOperationStr = "serviceOrdersByBranchID";
      } else if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
          sortDirection: "DESC",
        };
        nameOperation = serviceOrdersByCompanyID;
        nameOperationStr = "serviceOrdersByCompanyID";
      }

      if (isTechnician) userExecutor = me.id;

      const serviceOrdersData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          limit:
            !keywords && !userExecutor && searchBy.status?.length <= 0
              ? 10
              : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      code: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                    {
                      title: { contains: key },
                    },
                  ],
                })),
                ...(userExecutor ? { userExecutor: { eq: userExecutor } } : {}),
                ...(searchBy.status?.length > 0
                  ? {
                      or: searchBy.status.map((st) => ({ status: { eq: st } })),
                    }
                  : {}),
                deletedAt: { attributeExists: false },
              }
            : {
                ...(userExecutor ? { userExecutor: { eq: userExecutor } } : {}),
                ...(searchBy.status?.length > 0
                  ? {
                      or: searchBy.status.map((st) => ({ status: { eq: st } })),
                    }
                  : {}),
                deletedAt: { attributeExists: false },
              },
        })
      );
      const serviceOrdersItems = serviceOrdersData.data[nameOperationStr].items;
      setServiceOrders(serviceOrdersItems);
      getNamesByServiceOrders(serviceOrdersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: serviceOrdersData.data[nameOperationStr].nextToken,
        };
      });
      setServiceOrdersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setServiceOrdersLoading(false);
    }
  }

  async function serviceOrderRoutinesDelete(serviceOrderId) {
    try {
      const results = await API.graphql(
        graphqlOperation(serviceOrderMaintenanceRoutinesByServiceOrderId, {
          serviceOrderId,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.serviceOrderMaintenanceRoutinesByServiceOrderId.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(deleteServiceOrderMaintenanceRoutine, {
              input: { id: res.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con rutinas", err);
    }
  }

  async function serviceOrderToolsDelete(serviceOrderId) {
    try {
      const results = await API.graphql(
        graphqlOperation(serviceOrderToolsByServiceOrderId, {
          serviceOrderId,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.serviceOrderToolsByServiceOrderId.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(deleteServiceOrderTool, {
              input: { id: res.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con herramientas", err);
    }
  }

  async function serviceOrderEquipmentsDelete(serviceOrderId) {
    try {
      const results = await API.graphql(
        graphqlOperation(serviceOrderEquipmentsByServiceOrderId, {
          serviceOrderId,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.serviceOrderEquipmentsByServiceOrderId.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(deleteServiceOrderEquipment, {
              input: { id: res.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con equipos", err);
    }
  }

  async function serviceOrderSparesDelete(serviceOrderID) {
    try {
      const results = await API.graphql(
        graphqlOperation(sparesByServiceOrderID, {
          serviceOrderID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete = results.data.sparesByServiceOrderID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateSpare, {
              input: { id: res.id, serviceOrderID: null },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con repuestos", err);
    }
  }

  async function serviceOrderDelete(id) {
    try {
      setLoading(true);

      await serviceOrderRoutinesDelete(id);
      await serviceOrderToolsDelete(id);
      await serviceOrderSparesDelete(id);
      await serviceOrderEquipmentsDelete(id);

      const result = await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateServiceOrder.id;
      setServiceOrders(serviceOrders.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        serviceOrderId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  async function serviceOrderCancel(id) {
    try {
      setLoading(true);

      await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: { id, status: serviceOrderStatus.canceled },
        })
      );
      toast.success(strings.successCanceled);
      setServiceOrders(
        serviceOrders.map((serv) =>
          serv.id === id
            ? { ...serv, status: serviceOrderStatus.canceled }
            : serv
        )
      );
      setModalDetails({
        visible: false,
        serviceOrderId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchServiceOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id, searchBy.status]);

  const tableColumns = [
    {
      key: "status",
      label: "",
      _style: { width: "5%" },
    },
    ...(window.innerWidth > 768
      ? [
          {
            key: "companyName",
            label: strings.table.headers.companyName,
            _style: { width: "20%" },
          },
        ]
      : []),
    ...(window.innerWidth > 768
      ? [
          {
            key: "branchName",
            label: strings.table.headers.branchName,
            _style: { width: "20%" },
          },
        ]
      : []),
    ...(window.innerWidth > 768
      ? [
          {
            key: "userExecutorName",
            label: strings.table.headers.leaderExecutor,
            _style: { width: "15%" },
          },
        ]
      : []),
    ...(window.innerWidth > 768
      ? [
          {
            key: "title",
            label: strings.table.headers.name,
            _style: { width: "20%" },
          },
        ]
      : []),
    ...(window.innerWidth <= 768
      ? [
          {
            key: "orders",
            label: strings.table.headers.orders,
            _style: { width: "20%" },
          },
        ]
      : []),
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <Flex justifyContent="space-between">
        <span className="fs-3">{strings.title}</span>
        {canCreate && (
          <Button
            style={styles.buttonAdd}
            children={strings.serviceOrders.addServiceOrder}
            variation="primary"
            onClick={() => openModal("create")}
          />
        )}
      </Flex>
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter searchBy={searchBy} setSearchBy={setSearchBy} />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
          {!isTechnician && (
            <SelectUser
              placeholder={strings.headers.searchTechnician}
              onSelect={onSelectUser}
            />
          )}
          <MultipleSelect
            options={Object.entries(globalStrings.serviceOrderStatus).map(
              (opt) => ({ label: opt[1], value: opt[0] })
            )}
            values={searchBy.status}
            onChange={onChangeStatus}
            placeholder={strings.table.search.byState}
          />
        </Grid>
        {serviceOrdersLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(
          modalDetails.type === "cancel"
            ? strings.modal.confirmCancelQuestion
            : strings.modal.confirmDeleteQuestion,
          {
            name: modalDetails.serviceOrderCode,
          }
        )}
        onCancel={() =>
          setModalDetails({ visible: false, serviceOrderId: null })
        }
        onConfirm={() =>
          modalDetails.type === "cancel"
            ? serviceOrderCancel(modalDetails.serviceOrderId)
            : serviceOrderDelete(modalDetails.serviceOrderId)
        }
      />
      {modalServiceOrder.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalServiceOrder.visible}
          onClose={() => setModalServiceOrder({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalServiceOrder.action === "create" &&
                strings.modalServiceOrder.addTitle}
              {modalServiceOrder.action === "update" &&
                strings.modalServiceOrder.updateTitle}
              {modalServiceOrder.action === "execute" &&
                strings.modalServiceOrder.executeTitle}
              {modalServiceOrder.action === "export" &&
                strings.modalServiceOrder.exportTitle}
              {modalServiceOrder.action === "resume" &&
                strings.modalServiceOrder.resumeTitle}
              {modalServiceOrder.action === "read-photo" &&
                strings.modalServiceOrder.readPhotosTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalServiceOrder.action === "create" && (
                <ServiceOrderCreateForm onSuccess={onSuccessServiceOrder} />
              )}
              {modalServiceOrder.action === "update" && (
                <ServiceOrderUpdateForm
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                />
              )}
              {modalServiceOrder.action === "execute" && (
                <ExecutedServiceOrderContainer
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                />
              )}
              {modalServiceOrder.action === "export" && (
                <ExecutedServiceOrderContainer
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                  viewType="export"
                />
              )}
              {modalServiceOrder.action === "resume" && (
                <ServiceOrderResume
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                />
              )}
              {modalServiceOrder.action === "read-photo" && (
                <ServiceOrderPhotos
                  serviceOrderID={modalServiceOrder.serviceOrderId}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {loading && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Ordenes de Servicio",
    successDeleted: "Orden de Servicio eliminada con éxito",
    successCanceled: "Orden de Servicio anulada con éxito",
    headers: {
      searchTechnician: "Buscar técnico",
    },
    serviceOrders: {
      addServiceOrder: "Agregar Orden de Servicio",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      execute: "Ejecutar Orden",
      export: "Exportar Orden",
      delete: "Eliminar",
      cancel: "Anular Orden",
      resume: "Ver Resumen",
      readPhotos: "Ver Fotos",
    },
    responses: {
      errorFetch: "Error al consultar orden de servicio",
    },
    table: {
      headers: {
        name: "Nombre",
        leaderExecutor: "Líder Ejecutor",
        companyName: "Empresa",
        branchName: "Sede",
        orders: "Ordenes",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
        byState: "Buscar por estado",
      },
    },
    details: {
      title: "Detalles de la Orden de Servicio",
      description: "Descripción",
      billDate: "Fecha de facturación",
      billValue: "Valor de la factura",
      billCurrency: "Moneda de la factura",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar la orden de servicio {name}?",
      confirmCancelQuestion:
        "¿Estás seguro de anular la orden de servicio {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalServiceOrder: {
      addTitle: "Agregar Orden de Servicio",
      updateTitle: "Actualizar Orden de Servicio",
      executeTitle: "Ejecutar Orden de Servicio",
      exportTitle: "Exportar Orden de Servicio",
      resumeTitle: "Resumen de la Orden",
      readPhotosTitle: "Fotos de la Orden",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
};

export default ServiceOrders;
